import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Stack,
  Fade,
  useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PaymentIcon from '@mui/icons-material/Payment';
import PrintIcon from '@mui/icons-material/Print';
import EmailIcon from '@mui/icons-material/Email';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import { 
  buscarDocumentosServidor, 
  buscarPdfParaAssinatura,
  assinarDocumento 
} from '../../services/api';
import toast from 'react-hot-toast';

interface Document {
  id: number;
  nome_arquivo: string;
  tipo: string;
  status: string;
  data_assinatura: string;
  created_at: string;
  tamanho: number;
  hash_assinatura?: string;
}

interface DocumentPageProps {
  servidorData?: any; // Pode vir do localStorage ou API
}

const DocumentPage: React.FC<DocumentPageProps> = () => {
  const { servidorId } = useParams<{ servidorId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [servidorData, setServidorData] = useState<any>(null);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [pdfData, setPdfData] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!servidorId) {
        navigate('/consulta');
        return;
      }

      setLoading(true);
      try {
        // Buscar documentos do servidor
        const docs = await buscarDocumentosServidor(parseInt(servidorId));
        setDocuments(docs);
        
        if (docs.length > 0) {
          setSelectedDocument(docs[0]);
        }
        
        // Buscar dados do servidor do localStorage
        const storedData = localStorage.getItem('servidorData');
        if (storedData) {
          setServidorData(JSON.parse(storedData));
        }
      } catch (error: any) {
        console.error('Erro ao carregar documentos:', error);
        toast.error(error.response?.data?.message || 'Erro ao carregar documentos');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [servidorId, navigate]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleViewPdf = async (documentId: number) => {
    try {
      toast.loading('Carregando documento...', { id: 'pdf-load' });
      const response = await buscarPdfParaAssinatura(
        documentId, 
        parseInt(servidorId!)
      );
      
      setPdfData(response.pdf_base64);
      toast.success('Documento carregado!', { id: 'pdf-load' });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar PDF', { id: 'pdf-load' });
    }
  };

  const handleSignDocument = async (documentId: number) => {
    try {
      toast.loading('Assinando documento...', { id: 'sign' });
      // Simular hash de assinatura (em produção, use uma biblioteca real)
      const hashAssinatura = `signature_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      await assinarDocumento(
        documentId,
        parseInt(servidorId!),
        servidorData?.cpf || '',
        hashAssinatura
      );
      
      toast.success('Documento assinado com sucesso!', { id: 'sign' });
      
      // Recarregar documentos
      const docs = await buscarDocumentosServidor(parseInt(servidorId!));
      setDocuments(docs);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao assinar documento', { id: 'sign' });
    }
  };

  const handleDownload = (base64Data: string, filename: string) => {
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${base64Data}`;
    link.download = filename;
    link.click();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3, color: theme.palette.primary.main }} />
          <Typography variant="h6" color="text.secondary">
            Carregando seus documentos...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={500}>
      <Box>
        {/* Cabeçalho */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <DescriptionIcon 
            sx={{ 
              fontSize: 60, 
              color: 'primary.main',
              mb: 2 
            }} 
          />
          <Typography variant="h4" color="primary.main" gutterBottom fontWeight="bold">
            Meus Documentos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Visualize e assine seus documentos
          </Typography>
        </Box>

        {/* Informações do Servidor */}
        {servidorData && (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="primary.main">
                  {servidorData.nome_completo}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip 
                    label={`CPF: ${servidorData.cpf}`} 
                    size="small" 
                    variant="outlined" 
                  />
                  <Chip 
                    label={`ID: ${servidorId}`} 
                    size="small" 
                    variant="outlined" 
                  />
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Documentos */}
        <Grid container spacing={4}>
          {/* Lista de Documentos */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom color="primary.main">
                <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Documentos Disponíveis
              </Typography>
              
              {documents.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Nenhum documento disponível.
                </Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Documento</TableCell>
                        <TableCell align="right">Status</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {documents.map((doc) => (
                        <TableRow 
                          key={doc.id}
                          hover
                          selected={selectedDocument?.id === doc.id}
                          onClick={() => setSelectedDocument(doc)}
                          sx={{ 
                            cursor: 'pointer',
                            '&:last-child td, &:last-child th': { border: 0 } 
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {doc.nome_arquivo}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(doc.created_at)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={doc.status} 
                              size="small"
                              color={
                                doc.status === 'ASSINADO' ? 'success' : 
                                doc.status === 'PENDENTE' ? 'warning' : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewPdf(doc.id);
                              }}
                            >
                              Visualizar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* Visualização/Assinatura do Documento */}
          <Grid item xs={12} md={7}>
            {selectedDocument ? (
              <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                <Typography variant="h6" color="primary.main" gutterBottom>
                  {selectedDocument.nome_arquivo}
                </Typography>
                
                <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Tipo
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedDocument.tipo}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Status
                        </Typography>
                        <Chip 
                          label={selectedDocument.status} 
                          size="small"
                          color={
                            selectedDocument.status === 'ASSINADO' ? 'success' : 
                            selectedDocument.status === 'PENDENTE' ? 'warning' : 'default'
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Criado em
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(selectedDocument.created_at)}
                        </Typography>
                      </Grid>
                      {selectedDocument.data_assinatura && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Assinado em
                          </Typography>
                          <Typography variant="body1">
                            {formatDate(selectedDocument.data_assinatura)}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>

                {/* Visualizador de PDF */}
                {pdfData ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Pré-visualização do documento:
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: 400,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 2,
                        overflow: 'hidden',
                        mb: 2,
                      }}
                    >
                      <iframe
                        src={`data:application/pdf;base64,${pdfData}`}
                        title={selectedDocument.nome_arquivo}
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                      />
                    </Box>
                    
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownload(pdfData, selectedDocument.nome_arquivo)}
                      >
                        Baixar PDF
                      </Button>
                      
                      {selectedDocument.status === 'PENDENTE' && (
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => handleSignDocument(selectedDocument.id)}
                        >
                          Assinar Documento
                        </Button>
                      )}
                    </Stack>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <DescriptionIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      Nenhum documento carregado
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Clique em "Visualizar" para carregar o documento
                    </Typography>
                  </Box>
                )}
              </Paper>
            ) : (
              <Paper 
                sx={{ 
                  p: 6, 
                  borderRadius: 3, 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center'
                }}
              >
                <DescriptionIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nenhum documento selecionado
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Selecione um documento da lista ao lado para visualizar
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );
};

export default DocumentPage;